import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { servicesPath } from "../lib/paths";
import retrato from "./retrato-cesar.jpg";

const T = {
  de: {
    rotulo: "Der Verlag",
    titulo: "Ich mache meine Bücher selbst — von der ersten Korrektur bis zum Strichcode.",
    parrafos: [
      "Ich heiße César J. Cárdenas Lara. Geboren in Bogotá, seit 2007 in Österreich, seit 2012 im Bezirk Perg. Ich bin Biologe, habe einen Master für Spanisch als Fremdsprache und hier in Österreich katholische Religion studiert: alle drei Fächer unterrichte ich auch. Geschrieben habe ich immer, auf Spanisch und auf Deutsch, jahrelang nur für mich. Seit 2026 traue ich mich, es auch herauszugeben — dafür gibt es diesen Verlag.",
      "Der Verlag hat kein Büro. Er hat einen Tisch, einen Rechner und ziemlich viele Abendstunden. Jedes Buch entsteht hier zur Gänze — Lektorat, Satz, Umschlag und die Druckdaten —, und keines geht in den Druck, bevor es nicht Seite für Seite gelesen wurde.",
      "Eines noch, weil ich oft danach gefragt werde: Der Verlag bringt nur meine eigenen Bücher heraus. Ich suche keine fremden Manuskripte und verlege sie auch nicht unter meinem Namen. Etwas anderes ist es, an einem fertigen Text zu arbeiten — lektorieren, setzen, übersetzen. Das mache ich auf Anfrage, und dafür gibt es die Werkstatt.",
    ],
    datos: [
      ["Sitz", "Pabneukirchen, Oberösterreich"],
      ["Sprachen", "Spanisch · Deutsch"],
      ["ISBN-Präfix", "978-3-9505928"],
      ["Erster Titel", "2026"],
    ],
    werkstattTitulo: "Und eine Werkstatt, für die Bücher der anderen",
    werkstattTexto:
      "Was ich für andere mache, ist nicht verlegen, sondern fertigstellen: Lektorat, Stil, Satz und Übersetzung zwischen Spanisch und Deutsch — damit Sie dort veröffentlichen, wo Sie wollen: im Selbstverlag, bei einem anderen Verlag oder über die Plattform Ihrer Wahl.",
    werkstattCta: "Zur Werkstatt",
    retratoAlt: "César J. Cárdenas Lara, Verleger und Autor des Blauer Wolf Verlags",
  },
  es: {
    rotulo: "La editorial",
    titulo: "Hago mis libros yo, de la primera corrección al código de barras.",
    parrafos: [
      "Me llamo César J. Cárdenas Lara. Nací en Bogotá, vivo en Austria desde 2007 y en el Bezirk Perg desde 2012. Soy licenciado en biología, tengo un máster en enseñanza del español como lengua extranjera y estudios de religión católica en Austria: doy clases de las tres. Escribir ha sido mi afición de siempre, en español y en alemán, durante años solo para mí. Desde 2026 me atrevo a publicarlo, y para eso está este sello.",
      "La editorial no tiene oficina: tiene una mesa, un ordenador y bastantes horas de noche. Cada libro se hace entero aquí —la corrección, la maquetación, la cubierta y los archivos que van a imprenta—, y ninguno entra en prensa sin haberse leído antes página por página.",
      "Una aclaración, porque me la preguntan a menudo: el sello publica solo mis libros. No busco manuscritos ajenos ni los edito bajo mi nombre. Otra cosa es trabajar un texto que ya existe —corregirlo, maquetarlo, traducirlo—: eso sí lo hago por encargo, y para eso está el taller.",
    ],
    datos: [
      ["Sede", "Pabneukirchen, Alta Austria"],
      ["Idiomas", "Español · Alemán"],
      ["Prefijo ISBN", "978-3-9505928"],
      ["Primer título", "2026"],
    ],
    werkstattTitulo: "Y un taller, para los libros de los demás",
    werkstattTexto:
      "Lo que hago para otros no es publicarlos: es dejarles el libro listo. Corrección, estilo, maquetación y traducción entre el español y el alemán, para que publiques donde tú quieras: por tu cuenta, con otra editorial o en la plataforma que prefieras.",
    werkstattCta: "Ver el taller",
    retratoAlt: "César J. Cárdenas Lara, editor y autor del Blauer Wolf Verlag",
  },
};

export default function Verlag({ language = "de" }) {
  const t = T[language];

  return (
    <section id="verlag" className="bg-papel text-tinta py-20 sm:py-28 scroll-mt-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 lg:gap-20"
        >
          <div>
            <p className="rotulo text-loboOscuro mb-5">{t.rotulo}</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight tracking-titulo mb-8 max-w-2xl">
              {t.titulo}
            </h2>
            {t.parrafos.map((p, i) => (
              <p key={i} className="max-w-prosa text-tinta/85 mb-5">
                {p}
              </p>
            ))}
          </div>

          {/* El retrato va encima de la ficha, en la columna estrecha: la cara
              primero y los datos debajo, que es el orden en que se mira. */}
          <div className="lg:w-64 shrink-0 self-start">
            <img
              src={retrato}
              alt={t.retratoAlt}
              width="512"
              height="640"
              loading="lazy"
              className="w-44 sm:w-52 lg:w-full mx-auto lg:mx-0 shadow-[0_14px_40px_-16px_rgba(18,16,14,0.55)]"
            />

            <dl className="mt-10 border-t border-tinta/20 pt-6">
              {t.datos.map(([k, v]) => (
                <div key={k} className="py-3 border-b border-tinta/10">
                  <dt className="rotulo text-sombra mb-1">{k}</dt>
                  <dd className="text-tinta/90">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="mt-16 pt-10 border-t border-tinta/15 flex flex-col sm:flex-row sm:items-end gap-6 justify-between"
        >
          <div className="max-w-prosa">
            <h3 className="font-serif text-2xl mb-3">{t.werkstattTitulo}</h3>
            <p className="text-tinta/80">{t.werkstattTexto}</p>
          </div>
          <Link
            to={servicesPath(language)}
            className="rotulo shrink-0 text-loboOscuro border-b border-loboOscuro/40 hover:border-loboOscuro pb-1 transition-colors"
          >
            {t.werkstattCta} →
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
